import {CommentsPreference, DiffLineSpacingPreference} from '@github-ui/diff-view-settings/types'
import type {PullRequestFileTreeDiff} from '../page-data/payloads/file-tree'

export const COMPACT_LINE_HEIGHT = 20
export const RELAXED_LINE_HEIGHT = 25
export const DIFF_HEADER_SIZE = 42

// Gap between diff entries in the virtualized list
// Matches Primer's gap-3 utility class (var(--base-size-16))
export const DIFF_LIST_GAP = 16

// Thread component height constants based on actual rendered component sizes:

// Thread header row height (collapse button + "Comment on line R42" + badges)
// From InlineReviewThread.module.css: .ReviewThreadContainer { height: var(--base-size-32) }
export const THREAD_HEADER_HEIGHT = 32

// Reactions row height when present
// ReactionViewerBase typically renders at ~32px
export const REACTIONS_HEIGHT = 32

// Reply button area: container padding (p-2 = 16px) + CompactCommentButton (--control-medium-size = 32px)
export const REPLY_BUTTON_AREA_HEIGHT = 48

// Resolve button row: padding (p-2) + button + border-top
export const RESOLVE_BUTTON_ROW_HEIGHT = 48

// Comment content height: header (~32px) + body (~48px) + padding (~16px)
export const COMMENT_CONTENT_HEIGHT = 96

// Base height for a single-comment thread (expanded, with reactions):
// - Thread header: 32px
// - Comment content: 96px
// - Reactions: 32px
// - Reply button area: 48px
// - Resolve button row: 48px
// Total: ~256px
export const ESTIMATED_THREAD_BASE_HEIGHT =
  THREAD_HEADER_HEIGHT +
  COMMENT_CONTENT_HEIGHT +
  REACTIONS_HEIGHT +
  REPLY_BUTTON_AREA_HEIGHT +
  RESOLVE_BUTTON_ROW_HEIGHT

// Each additional reply adds: comment content (~96px) + reactions (~32px)
// Note: replies share the thread header, reply button, and resolve button with the root comment
export const ESTIMATED_REPLY_HEIGHT = COMMENT_CONTENT_HEIGHT + REACTIONS_HEIGHT

// Annotation height estimates based on actual rendered component:
// - Banner with icon + app name: ~32px
// - Title: ~24px
// - Message (8-line clamp max, typically 2-4 lines): ~64px
// - Source + details button: ~40px
// - Padding: ~16px
// Total: ~176px, but annotations are often collapsed by default
export const ESTIMATED_ANNOTATION_HEIGHT = 120

// Legacy height estimates used when accurate estimation is disabled (feature flag off)
// These are intentionally larger to avoid undersizing when we don't have reply counts
export const LEGACY_COMMENT_HEIGHT = 500
export const LEGACY_ANNOTATION_HEIGHT = 300

export const ESTIMATED_CONTEXT_LINES = 6
export const ESTIMATED_LINES_PER_HUNK = 5
export const DELETED_FILE_HEIGHT = 135
export const TOO_BIG_HEIGHT_PLACEHOLDER_HEIGHT = 133
export const TOO_BIG_LINE_LIMIT = 400

// Estimated rendered height of an image/binary diff (header + a typically-sized image preview).
// Binary diffs report ~0 changed lines, so without this they estimate ~1 line tall and then
// jump dramatically once the real image is measured. This biases the initial estimate toward a
// realistic mid-size preview to reduce scroll jank (see github/pull-requests#24555).
export const ESTIMATED_IMAGE_DIFF_HEIGHT = 300

// Raster image extensions rendered as image previews in the diff view.
// SVG is intentionally excluded because SVG diffs typically render as text/code lines.
const IMAGE_FILE_EXTENSIONS = new Set(['png', 'jpg', 'jpeg', 'gif', 'webp', 'bmp', 'ico', 'avif', 'tif', 'tiff'])

/**
 * Returns true when the path points to a raster image that the diff view renders
 * as an image preview (as opposed to a text diff).
 */
export function isImagePath(path: string | undefined): boolean {
  if (!path) return false
  const lastDot = path.lastIndexOf('.')
  if (lastDot < 0) return false
  const extension = path.slice(lastDot + 1).toLowerCase()
  return IMAGE_FILE_EXTENSIONS.has(extension)
}

// Collapsed threads show only the header row (collapse icon + "Comment on line R42" + badges)
export const COLLAPSED_THREAD_HEIGHT = THREAD_HEADER_HEIGHT + 2 // +2 for border

// Height per line of comment text (accounts for line-height in markdown rendering)
export const COMMENT_LINE_HEIGHT = 24

// Gap between comments in a thread (--base-size-6 from CSS)
export const COMMENT_GAP = 6

// ============================================================================
// OVERESTIMATION BUFFERS FOR TANSTACK OPTIMIZATION
// ============================================================================
// For react-virtual: overestimate → smooth upward shift, underestimate → jarring downward jump
// We bias toward slight overestimation for better UX

// Per-comment buffer for markdown rendering effects not captured by line counting:
// - Code blocks: ~16-24px padding + borders
// - Blockquotes: ~8px border-left + padding
// - Images (very common in PR comments): unpredictable, assume small thumbnail
// - Lists: bullet/number width + spacing
// - Horizontal rules: 16px margin
export const COMMENT_MARKDOWN_BUFFER = 32

// Per-thread buffer for container chrome we might have underestimated
export const THREAD_CHROME_BUFFER = 16

// Buffer for annotations (can have expandable details)
export const ANNOTATION_BUFFER = 24

// Split view shows lines side-by-side, roughly 60% of unified height for the same content
export const SPLIT_VIEW_HEIGHT_FACTOR = 0.6

/**
 * Thread sizing info for accurate height estimation.
 */
export type ThreadSizeInfo = {
  replyCount: number
  /** Whether the thread is visually collapsed (not the same as resolved) */
  isCollapsed: boolean
  /** Estimated lines of text across all comments in the thread */
  estimatedLines?: number
}

export type ThreadSizeInfoMap = Map<number, ThreadSizeInfo>

/**
 * Estimates the rendered height (in pixels) of a diff view for a file in a pull request.
 *
 * This function calculates an approximate height based on several factors:
 * - Number of changed lines in the diff
 * - User's line spacing preference (compact vs relaxed)
 * - Number and type of comments/annotations (if visible)
 * - Thread reply counts and collapsed states
 * - Split vs unified view mode
 *
 * The estimation is used for virtualization and performance optimization when rendering
 * large pull request diffs.
 */
export function estimateDiffSize({
  diffSummary,
  commentPreference,
  lineSpacingPreference,
  isCollapsed,
  threadSizeInfoMap,
  isSplitView = false,
  imageEstimateEnabled = false,
}: {
  diffSummary: PullRequestFileTreeDiff | undefined
  commentPreference: CommentsPreference
  lineSpacingPreference: DiffLineSpacingPreference
  isCollapsed: boolean
  /** Map of thread IDs to sizing info (reply count + collapsed status) */
  threadSizeInfoMap?: ThreadSizeInfoMap
  /** Whether the diff is displayed in split view mode */
  isSplitView?: boolean
  /** When enabled, image/binary files use a realistic preview height estimate */
  imageEstimateEnabled?: boolean
}) {
  const heightPerLine =
    lineSpacingPreference === DiffLineSpacingPreference.Compact ? COMPACT_LINE_HEIGHT : RELAXED_LINE_HEIGHT

  if (isCollapsed) {
    return DIFF_HEADER_SIZE
  }

  const diffIsProbablyTooBig = diffSummary?.linesChanged && diffSummary.linesChanged > TOO_BIG_LINE_LIMIT
  if (diffIsProbablyTooBig) {
    return DIFF_HEADER_SIZE + TOO_BIG_HEIGHT_PLACEHOLDER_HEIGHT
  }

  // Deleted files are not rendered by default.
  if (diffSummary?.changeType === 'DELETED' || diffSummary?.changeType === 'REMOVED') {
    return DELETED_FILE_HEIGHT + DIFF_HEADER_SIZE
  }

  // Image/binary files render as a preview rather than text lines, so their changed-line
  // count (~0) badly underestimates their height. Use a realistic preview estimate instead.
  if (imageEstimateEnabled && isImagePath(diffSummary?.path)) {
    return DIFF_HEADER_SIZE + ESTIMATED_IMAGE_DIFF_HEIGHT
  }

  // Estimate a buffer for context lines on modified files.
  const estimatedHunks = diffSummary ? Math.max(1, Math.ceil(diffSummary.linesChanged / ESTIMATED_LINES_PER_HUNK)) : 1
  const estimatedContextLinesHeight =
    diffSummary?.changeType === 'MODIFIED' ? estimatedHunks * ESTIMATED_CONTEXT_LINES * heightPerLine : 0

  // Estimate comment & annotations height from the markerMap
  let estimatedCommentHeight = 0
  let estimatedAnnotationHeight = 0
  if (commentPreference === CommentsPreference.Visible) {
    let annotationCount = 0

    const markers = Object.values(diffSummary?.markersMap || {})
    for (const markerPosition of markers) {
      for (const threadMarker of markerPosition.threads) {
        // When threadSizeInfoMap is not provided (feature flag off), use legacy flat-rate estimation
        if (!threadSizeInfoMap) {
          estimatedCommentHeight += LEGACY_COMMENT_HEIGHT
          continue
        }

        const sizeInfo = threadSizeInfoMap.get(threadMarker.id)

        // Collapsed threads show only a small indicator
        if (sizeInfo?.isCollapsed) {
          estimatedCommentHeight += COLLAPSED_THREAD_HEIGHT
          continue
        }

        const replyCount = sizeInfo?.replyCount ?? 0
        const commentCount = 1 + replyCount

        // If we have estimated lines from comment body analysis, use that for more accuracy
        if (sizeInfo?.estimatedLines) {
          // Thread chrome: header + reactions per comment + reply button + resolve button
          const threadChromeHeight =
            THREAD_HEADER_HEIGHT +
            commentCount * REACTIONS_HEIGHT +
            REPLY_BUTTON_AREA_HEIGHT +
            RESOLVE_BUTTON_ROW_HEIGHT

          // Comment text height based on estimated lines
          const textHeight = sizeInfo.estimatedLines * COMMENT_LINE_HEIGHT

          // Gaps between comments (n-1 gaps for n comments)
          const gapHeight = Math.max(0, commentCount - 1) * COMMENT_GAP

          // Overestimation buffer: markdown rendering effects + thread chrome buffer
          const markdownBuffer = commentCount * COMMENT_MARKDOWN_BUFFER
          const overestimationBuffer = markdownBuffer + THREAD_CHROME_BUFFER

          estimatedCommentHeight += threadChromeHeight + textHeight + gapHeight + overestimationBuffer
        } else {
          // Fallback: use base height + reply height formula (already includes generous estimates)
          estimatedCommentHeight += ESTIMATED_THREAD_BASE_HEIGHT + THREAD_CHROME_BUFFER
          if (replyCount > 0) {
            estimatedCommentHeight += replyCount * ESTIMATED_REPLY_HEIGHT
            // Add gaps between comments
            estimatedCommentHeight += replyCount * COMMENT_GAP
          }
        }
      }
      annotationCount += markerPosition.annotations.length
    }

    // Use accurate or legacy annotation height based on whether threadSizeInfoMap is provided
    // Add buffer for expandable details in annotations
    const annotationHeight = threadSizeInfoMap
      ? ESTIMATED_ANNOTATION_HEIGHT + ANNOTATION_BUFFER
      : LEGACY_ANNOTATION_HEIGHT
    estimatedAnnotationHeight = annotationCount * annotationHeight
  }

  // Added files don't have context lines
  let linesChanged = diffSummary?.linesChanged ?? 1
  if (diffSummary?.changeType === 'ADDED') {
    linesChanged += 1
  }
  let lineHeight = heightPerLine * linesChanged

  // Split view displays left/right side-by-side, reducing overall height
  if (isSplitView) {
    lineHeight = Math.ceil(lineHeight * SPLIT_VIEW_HEIGHT_FACTOR)
  }

  return (
    DIFF_HEADER_SIZE + lineHeight + estimatedCommentHeight + estimatedAnnotationHeight + estimatedContextLinesHeight
  )
}
